import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users, Building2, Briefcase, FileText, TrendingUp, Activity,
    ArrowUpRight, Banknote, Wallet, Calendar, Filter,
    ChevronRight, Info, CheckCircle2, AlertCircle, Clock, Download
} from 'lucide-react';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Legend, AreaChart, Area
} from 'recharts';
import api from '../utils/api';

const StatCard = ({ title, value, icon: Icon, trend, color = "indigo" }) => {
    const colors = {
        indigo: "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800",
        emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800",
        amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400 border-amber-100 dark:border-amber-800",
        rose: "bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400 border-rose-100 dark:border-rose-800",
        slate: "bg-slate-50 text-slate-600 dark:bg-slate-900/20 dark:text-slate-400 border-slate-100 dark:border-slate-800",
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all group"
        >
            <div className="flex items-center justify-between mb-4">
                <div className={`p-2.5 rounded-xl ${colors[color]} border transition-transform group-hover:scale-110 duration-300`}>
                    <Icon className="w-5 h-5" />
                </div>
                {trend && (
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold tracking-tight uppercase ${trend > 0 ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400'}`}>
                        {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <Activity className="w-3 h-3" />}
                        <span>{Math.abs(trend)}%</span>
                    </div>
                )}
            </div>
            <div className="space-y-1">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</h3>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-baseline gap-1">
                    {value}
                </h2>
            </div>
        </motion.div>
    );
};

const CustomTooltip = ({ active, payload, label, prefix = "", suffix = "" }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-slate-900/95 backdrop-blur-md border border-slate-800 p-3 rounded-lg shadow-2xl text-[11px] min-w-[120px]">
                <p className="font-bold text-slate-400 mb-2 border-b border-slate-800 pb-1">{label}</p>
                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center justify-between gap-4 py-1">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
                            <span className="text-slate-200 capitalize">{entry.name}</span>
                        </div>
                        <span className="font-mono text-white text-right font-bold">
                            {prefix}{entry.value.toLocaleString()}{suffix}
                        </span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedRoles, setSelectedRoles] = useState(['candidate', 'recruiter']);

    // Date range filter state
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const [dateRange, setDateRange] = useState({
        startMonth: '1',
        startYear: (currentYear - 1).toString(),
        endMonth: currentMonth.toString(),
        endYear: currentYear.toString()
    });

    useEffect(() => {
        document.title = 'Tableau de Bord | Tekhdem Admin';
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const params = {
                startDate: `${dateRange.startYear}-${dateRange.startMonth.padStart(2, '0')}-01`,
                endDate: `${dateRange.endYear}-${dateRange.endMonth.padStart(2, '0')}-31` // Approx end of month
            };
            const response = await api.get('/admin/stats', { params });
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        fetchStats();
    };

    const handleExport = async (type) => {
        try {
            const response = await api.get(`/admin/export/${type}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error(`Error exporting ${type}:`, error);
            alert('Erreur lors de l\'exportation du fichier CSV');
        }
    };

    const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());
    const months = [
        { val: '1', label: 'Jan' }, { val: '2', label: 'Fév' }, { val: '3', label: 'Mar' },
        { val: '4', label: 'Avr' }, { val: '5', label: 'Mai' }, { val: '6', label: 'Juin' },
        { val: '7', label: 'Juil' }, { val: '8', label: 'Août' }, { val: '9', label: 'Sep' },
        { val: '10', label: 'Oct' }, { val: '11', label: 'Nov' }, { val: '12', label: 'Déc' }
    ];

    const userGrowthData = useMemo(() => {
        if (!stats?.usersByMonth) return [];

        const transformed = {};
        stats.usersByMonth.forEach(item => {
            if (!transformed[item.name]) {
                transformed[item.name] = { name: item.name };
            }
            transformed[item.name][item.role] = item.count;
        });

        return Object.values(transformed);
    }, [stats]);

    const revenueDetailedData = useMemo(() => {
        if (!stats?.financials?.revenueByMonthDetailed) return { data: [], companies: [] };

        const transformed = {};
        const companies = new Set();

        stats.financials.revenueByMonthDetailed.forEach(item => {
            if (!transformed[item.name]) {
                transformed[item.name] = { name: item.name };
            }
            transformed[item.name][item.company] = item.value;
            companies.add(item.company);
        });

        return {
            data: Object.values(transformed),
            companies: Array.from(companies)
        };
    }, [stats]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-[70vh] space-y-4">
                <div className="relative w-16 h-16">
                    <div className="absolute inset-0 border-4 border-indigo-500/20 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="text-slate-500 font-medium animate-pulse">Chargement des données...</p>
            </div>
        );
    }

    const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#f43f5e', '#8b5cf6', '#06b6d4', '#ec4899'];
    const roleColors = {
        candidate: '#6366f1',
        recruiter: '#10b981'
    };

    const toggleRole = (role) => {
        setSelectedRoles(prev =>
            prev.includes(role)
                ? prev.filter(r => r !== role)
                : [...prev, role]
        );
    };

    return (
        <div className="max-w-[1600px] mx-auto space-y-10 py-6 px-2 lg:px-6">
            {/* Super Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-slate-200 dark:border-slate-800">
                <div className="space-y-1">

                    <h1 className="text-4xl font-extrabold text-slate-950 dark:text-white tracking-tight">
                        Dashboard
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm max-w-lg">
                        Surveillez les performances de la plateforme, l'engagement des utilisateurs et les flux financiers en temps réel.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <form onSubmit={handleFilterSubmit} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-2 flex flex-wrap items-center gap-3 shadow-md">
                        <div className="flex items-center gap-2 pl-2 pr-4 border-r border-slate-200 dark:border-slate-800">
                            <Calendar className="w-4 h-4 text-indigo-500" />
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Période</span>
                        </div>

                        <div className="flex items-center gap-1.5">
                            <select
                                value={dateRange.startMonth}
                                onChange={(e) => setDateRange(prev => ({ ...prev, startMonth: e.target.value }))}
                                className="bg-transparent text-xs font-semibold text-slate-700 dark:text-slate-300 outline-none cursor-pointer hover:text-indigo-600 transition-colors"
                            >
                                {months.map(m => <option key={m.val} value={m.val} className="bg-white dark:bg-slate-900">{m.label}</option>)}
                            </select>
                            <select
                                value={dateRange.startYear}
                                onChange={(e) => setDateRange(prev => ({ ...prev, startYear: e.target.value }))}
                                className="bg-transparent text-xs font-semibold text-slate-700 dark:text-slate-300 outline-none cursor-pointer hover:text-indigo-600 transition-colors"
                            >
                                {years.map(y => <option key={y} value={y} className="bg-white dark:bg-slate-900">{y}</option>)}
                            </select>
                        </div>

                        <div className="w-4 h-[1px] bg-slate-200 dark:bg-slate-800" />

                        <div className="flex items-center gap-1.5">
                            <select
                                value={dateRange.endMonth}
                                onChange={(e) => setDateRange(prev => ({ ...prev, endMonth: e.target.value }))}
                                className="bg-transparent text-xs font-semibold text-slate-700 dark:text-slate-300 outline-none cursor-pointer hover:text-indigo-600 transition-colors"
                            >
                                {months.map(m => <option key={m.val} value={m.val} className="bg-white dark:bg-slate-900">{m.label}</option>)}
                            </select>
                            <select
                                value={dateRange.endYear}
                                onChange={(e) => setDateRange(prev => ({ ...prev, endYear: e.target.value }))}
                                className="bg-transparent text-xs font-semibold text-slate-700 dark:text-slate-300 outline-none cursor-pointer hover:text-indigo-600 transition-colors"
                            >
                                {years.map(y => <option key={y} value={y} className="bg-white dark:bg-slate-900">{y}</option>)}
                            </select>
                        </div>

                        <button
                            type="submit"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl p-2 px-3 transition-all flex items-center gap-2 shadow-lg shadow-indigo-500/20 active:scale-95"
                        >
                            <Filter className="w-3.5 h-3.5" />
                            <span className="text-xs font-bold">Filtrer</span>
                        </button>
                    </form>

                    <div className="hidden lg:flex items-center gap-1 text-[10px] text-slate-500 bg-slate-50 dark:bg-slate-900/50 px-3 py-2 rounded-xl border border-slate-100 dark:border-slate-800">
                        <Clock className="w-3 h-3" />
                        MAJ: {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
            </div>

            {/* Principal Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <StatCard title="Candidats" value={stats?.users?.candidates?.toLocaleString() || 0} icon={Users} trend={12.5} color="indigo" />
                <StatCard title="Recruteurs" value={stats?.users?.recruiters?.toLocaleString() || 0} icon={Building2} trend={8.2} color="emerald" />
                <StatCard title="Missions Actives" value={stats?.missions?.active?.toLocaleString() || 0} icon={Briefcase} trend={4.1} color="amber" />
                <StatCard title="CA Total" value={`${stats?.financials?.totalRevenue?.toLocaleString() || 0} DZD`} icon={Wallet} color="rose" />
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">

                {/* Evolution des inscrits (Big Chart) */}
                <div className="xl:col-span-2 bg-white dark:bg-slate-950 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm shadow-indigo-500/5">
                    <div className="p-6 border-b border-slate-100 dark:border-slate-900 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Évolution des Inscriptions</h3>
                            <p className="text-xs text-slate-500 mt-1">Analyse comparative du recrutement utilisateur par mois</p>
                        </div>
                        <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-xl border border-slate-200 dark:border-slate-800">
                            <button
                                onClick={() => toggleRole('candidate')}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedRoles.includes('candidate') ? 'bg-white dark:bg-indigo-600 text-indigo-600 dark:text-white shadow-sm' : 'text-slate-500'}`}
                            >
                                <div className={`w-2 h-2 rounded-full ${selectedRoles.includes('candidate') ? 'bg-indigo-600 dark:bg-white' : 'bg-slate-300'}`} />
                                Candidats
                            </button>
                            <button
                                onClick={() => toggleRole('recruiter')}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedRoles.includes('recruiter') ? 'bg-white dark:bg-emerald-600 text-emerald-600 dark:text-white shadow-sm' : 'text-slate-500'}`}
                            >
                                <div className={`w-2 h-2 rounded-full ${selectedRoles.includes('recruiter') ? 'bg-emerald-600 dark:bg-white' : 'bg-slate-300'}`} />
                                Recruteurs
                            </button>
                        </div>
                    </div>
                    <div className="p-6 h-[400px]">
                        {userGrowthData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={userGrowthData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorCandidate" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1} />
                                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="colorRecruiter" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" opacity={0.5} />
                                    <XAxis
                                        dataKey="name"
                                        stroke="#94A3B8"
                                        fontSize={11}
                                        tickLine={false}
                                        axisLine={false}
                                        dy={10}
                                    />
                                    <YAxis
                                        stroke="#94A3B8"
                                        fontSize={11}
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(val) => Math.floor(val)}
                                    />
                                    <Tooltip content={<CustomTooltip suffix=" inscrits" />} />
                                    <AnimatePresence>
                                        {selectedRoles.includes('candidate') && (
                                            <Area
                                                type="monotone"
                                                dataKey="candidate"
                                                name="Candidats"
                                                stroke="#6366f1"
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill="url(#colorCandidate)"
                                                animationDuration={1500}
                                            />
                                        )}
                                        {selectedRoles.includes('recruiter') && (
                                            <Area
                                                type="monotone"
                                                dataKey="recruiter"
                                                name="Recruteurs"
                                                stroke="#10b981"
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill="url(#colorRecruiter)"
                                                animationDuration={1500}
                                            />
                                        )}
                                    </AnimatePresence>
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-2">
                                <Info className="w-8 h-8 opacity-20" />
                                <p className="text-sm">Données indisponibles pour cette période</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Applications Funnel / Distribution */}
                <div className="bg-white dark:bg-slate-950 rounded-3xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                    <div className="mb-6">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Traitement des Candidatures</h3>
                        <p className="text-xs text-slate-500 mt-1">Status global des demandes d'emploi</p>
                    </div>
                    <div className="h-[250px] relative">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'En attente', value: stats?.applications?.pending || 0 },
                                        { name: 'Acceptées', value: stats?.applications?.accepted || 0 },
                                        { name: 'Refusées', value: stats?.applications?.rejected || 0 },
                                    ]}
                                    innerRadius={70}
                                    outerRadius={90}
                                    paddingAngle={8}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    <Cell fill="#f59e0b" />
                                    <Cell fill="#10b981" />
                                    <Cell fill="#f43f5e" />
                                </Pie>
                                <Tooltip content={<CustomTooltip suffix=" dossiers" />} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-3xl font-black text-slate-900 dark:text-white">{stats?.applications?.total || 0}</span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total</span>
                        </div>
                    </div>
                    <div className="space-y-3 mt-4">
                        {[
                            { label: 'En traitement', value: stats?.applications?.pending || 0, color: 'bg-amber-500', icon: Clock },
                            { label: 'Succès', value: stats?.applications?.accepted || 0, color: 'bg-emerald-500', icon: CheckCircle2 },
                            { label: 'Non retenus', value: stats?.applications?.rejected || 0, color: 'bg-rose-500', icon: AlertCircle },
                        ].map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-3">
                                    <div className={`p-1.5 rounded-lg ${item.color} text-white`}>
                                        <item.icon className="w-3.5 h-3.5" />
                                    </div>
                                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{item.label}</span>
                                </div>
                                <span className="text-sm font-bold text-slate-900 dark:text-white">{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Financial Detailed Analysis (Full Width Bar Chart) */}
                <div className="xl:col-span-3 bg-white dark:bg-slate-950 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-slate-100 dark:border-slate-900 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/50 dark:bg-slate-900/50">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 rounded-full bg-rose-500" />
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Répartition du CA par Entreprise</h3>
                            </div>
                            <p className="text-xs text-slate-500">Chiffre d'affaires mensuel ventilé par client principal</p>
                        </div>
                    </div>
                    <div className="p-6 h-[400px]">
                        {revenueDetailedData.data.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={revenueDetailedData.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" opacity={0.5} />
                                    <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                                    <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(val) => `${val / 1000}k`} />
                                    <Tooltip content={<CustomTooltip suffix=" DZD" />} />
                                    <Legend
                                        verticalAlign="top"
                                        align="right"
                                        iconType="circle"
                                        wrapperStyle={{ fontSize: '11px', fontWeight: 'bold', paddingBottom: '20px' }}
                                    />
                                    {revenueDetailedData.companies.map((company, index) => (
                                        <Bar
                                            key={company}
                                            dataKey={company}
                                            stackId="a"
                                            fill={COLORS[index % COLORS.length]}
                                            radius={index === revenueDetailedData.companies.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                            barSize={45}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-2">
                                <Banknote className="w-8 h-8 opacity-20" />
                                <p className="text-sm">Aucun flux financier détecté sur cette période</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Secondary Metrics & Quick Actions */}
                <div className="xl:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-indigo-600 rounded-3xl p-6 text-white relative overflow-hidden group">
                        <div className="absolute -right-4 -top-4 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                        <h4 className="text-indigo-200 text-xs font-bold uppercase tracking-widest mb-4">Total Offres</h4>
                        <div className="flex items-end justify-between">
                            <h2 className="text-5xl text-white font-bold">{stats?.jobOffers?.total || 0}</h2>
                            <TrendingUp className="w-10 h-10 opacity-40" />
                        </div>
                        <p className="text-indigo-100 text-xs mt-4 flex items-center gap-1">
                            <span className="font-bold">{stats?.jobOffers?.active || 0}</span> annonces actuellement en ligne
                        </p>
                    </div>

                    <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 flex flex-col justify-between shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Activités Récentes</h4>
                            <button className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                                Voir tout <ChevronRight className="w-3 h-3" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            {(stats?.recentActivity || []).slice(0, 3).map((user, idx) => (
                                <div key={idx} className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center text-[10px] font-bold">
                                        {user.email.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{user.email}</p>
                                        <p className="text-[10px] text-slate-500 capitalize">{user.role}</p>
                                    </div>
                                    <div className="text-[9px] font-bold text-slate-400">
                                        {new Date(user.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-emerald-600 rounded-3xl p-6 text-white relative overflow-hidden group">
                        <div className="absolute -right-4 -top-4 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                        <h4 className="text-emerald-200 text-xs font-bold uppercase tracking-widest mb-4">Revenu Mensuel</h4>
                        <div className="flex items-end justify-between">
                            <h2 className="text-4xl text-white font-bold">{stats?.financials?.monthlyRevenue?.toLocaleString() || 0} DZD</h2>
                            <CheckCircle2 className="w-10 h-10 opacity-40" />
                        </div>
                        <p className="text-emerald-100 text-xs mt-4">Calculé sur les 30 derniers jours d'activité</p>
                    </div>
                </div>

                {/* Data Export Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="xl:col-span-3 bg-white dark:bg-slate-950 rounded-3xl border border-slate-200 dark:border-slate-800 p-8 shadow-sm mt-4"
                >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                        <div>
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Export de Données</h3>
                            <p className="text-sm text-slate-500 mt-1">Générez des rapports CSV complets de la plateforme</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                        {[
                            { label: 'Recruteurs', type: 'recruiters', color: 'indigo' },
                            { label: 'Candidats', type: 'candidates', color: 'indigo' },
                            { label: 'Chiffre d\'Affaires', type: 'turnover', color: 'emerald' },
                            { label: 'Offres d\'Emploi', type: 'job-offers', color: 'amber' },
                            { label: 'Offres Missions', type: 'missions', color: 'amber' },
                        ].map((item, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleExport(item.type)}
                                className="flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-slate-900/50 hover:bg-white dark:hover:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:border-indigo-500 dark:hover:border-indigo-500/50 rounded-2xl transition-all group active:scale-95 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10"
                            >
                                <div className="p-4 rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 mb-5 group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-600 transition-all duration-300 shadow-sm">
                                    <Download className="w-6 h-6" />
                                </div>
                                <span className="text-xs font-black text-slate-600 dark:text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 text-center uppercase tracking-widest">
                                    {item.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </motion.div>

            </div>
        </div>
    );
};

export default Dashboard;
